import { ClientOnboardingForm, useBrandex, type OnboardedClient } from "@brandex/dashboard";
import { Alert, Button } from "@brandex/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

/**
 * Onboard a client on their behalf (F-17): the doctor wizard's steps, then
 * an invite email so the doctor chooses their own password.
 */
export function ClientNewPage() {
  const { nav } = useBrandex();
  const qc = useQueryClient();
  const [done, setDone] = useState<{ client: OnboardedClient; email: string } | null>(null);
  // A fresh form per client, so nothing carries over after "Onboard another".
  const [round, setRound] = useState(0);
  return (
    <div className="mx-auto grid w-full max-w-3xl gap-6">
      <header>
        <h1 className="font-display text-3xl font-bold tracking-tight">Onboard a client</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Fill in the doctor&apos;s details. We create their account and profile and email them a link to choose a password.
        </p>
      </header>
      {done === null ? (
        <ClientOnboardingForm
          key={round}
          onDone={(client, email) => {
            void qc.invalidateQueries({ queryKey: ["admin", "clients"] });
            setDone({ client, email });
          }}
        />
      ) : (
        <div className="glass grid gap-4 rounded-[var(--radius-xl)] p-6">
          <h2 className="font-display text-xl font-bold">Client onboarded</h2>
          <Alert tone="success">
            {done.client.practice.profile.full_name}&apos;s profile is set up. We emailed {done.email} a link to choose a password. It is valid for 7 days; after that the doctor can use &ldquo;Forgot password&rdquo;.
          </Alert>
          <p className="text-sm text-muted-foreground">
            Package: {done.client.practice.subscription?.status ?? "none"}. Quote or activate it in Subscriptions. Website: <code>{done.client.practice.website.subdomain_url}</code>
          </p>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => nav.navigate("/clients")}>Back to clients</Button>
            <Button
              variant="outline"
              onClick={() => {
                setDone(null);
                setRound((r) => r + 1);
              }}
            >
              Onboard another client
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
